import { useEffect, useId, useState } from 'react';

import { Badge, Button, Icon, IconButton } from '@ds/core';
import { Alert, Modal, Tooltip } from '@ds/feedback';
import { Checkbox, FormField, Input, Select, Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { CampoDeFicha, FaixaDeClassificacao, ModeloDeFicha, TipoDeCampoDaFicha } from '@/data/tipos';
import { ESCALAS_ILUSTRADAS } from '@/data/escalasIlustradas';
import { ESCALAS_ENCADEADAS, PROTOCOLOS_DE_ADIPOMETRIA, TABELAS_SELECIONAVEIS, protocoloPorSlug } from '@/data/blocosDaFicha';
import { NOME_DO_TIPO, PALETA, PENDENTES, TIPO_TEM_ESCALA, TIPO_TEM_OPCOES, TIPO_TEM_PROTOCOLO, TIPO_TEM_SEXO, novoUid } from './paleta';
import { BlocoDeResposta } from './BlocoDeResposta';

export interface EditorDeModeloProps {
    open: boolean;
    /** `null` abre em branco. Com modelo, edita o que já existe. */
    modelo: ModeloDeFicha | null;
    /** Nome sugerido quando é duplicação — o modelo original não é alterado. */
    nomeInicial?: string;
    /** Campos vindos de um arquivo importado: entram no editor SEM salvar. */
    camposIniciais?: CampoDeFicha[];
    onClose: () => void;
    onSalvo: () => void;
}

/** Um campo recém-criado, com o rótulo em branco para quem monta preencher. */
function campoNovo(tipo: TipoDeCampoDaFicha): CampoDeFicha {
    return {
        uid: novoUid(),
        tipo,
        /* Os blocos do profissional já sabem como se chamam: o rótulo é o nome
           da escala, e apagá-lo para quem monta redigitar "Escala de Glogau" não
           ajuda ninguém. Os genéricos continuam nascendo em branco. */
        rotulo: rotuloPadrao(tipo),
        /* Bloco de seleção nasce SEM opção. Opção que aparece sozinha vira
           resposta que ninguém escolheu — e no prontuário isso é registro
           clínico inventado pelo formulário. */
        ...(TIPO_TEM_OPCOES.includes(tipo) ? { opcoes: [] } : {}),
        ...(tipo === 'escala' ? { escala: ESCALAS_ILUSTRADAS[0]?.slug } : {}),
        ...(tipo === 'escala-encadeada' ? { escala: ESCALAS_ENCADEADAS[0]?.slug } : {}),
        ...(tipo === 'tabela-selecionavel' ? { tabela: TABELAS_SELECIONAVEIS[0]?.slug } : {}),
        /* Nasce no somatório: é o único que não afirma percentual de gordura, e
           o construtor não deve escolher protocolo clínico por ninguém. */
        ...(tipo === 'adipometria' ? { protocolo: 'somatorio' as const } : {}),
        /* A tabela nasce com uma célula: uma grade 0×0 não deixa nem começar. */
        ...(tipo === 'tabela' ? { colunas: ['Coluna 1'], linhas: ['Linha 1'] } : {}),
        ...(TIPO_TEM_SEXO.includes(tipo) ? { sexo: '' as const } : {}),
    };
}

function rotuloPadrao(tipo: TipoDeCampoDaFicha): string {
    if (tipo === 'escala') return ESCALAS_ILUSTRADAS[0]?.nome ?? '';
    if (tipo === 'escala-encadeada') return ESCALAS_ENCADEADAS[0]?.rotulo ?? '';
    if (tipo === 'tabela-selecionavel') return TABELAS_SELECIONAVEIS[0]?.rotulo ?? '';
    if (tipo === 'calculo-imc') return 'Cálculo de IMC';
    if (tipo === 'adipometria') return 'Adipometria';
    if (tipo === 'perimetria') return 'Perimetria';
    return '';
}

/** O que impede o modelo de ser salvo, na ordem em que a pessoa deve resolver. */
function problemas(nome: string, campos: CampoDeFicha[]): string[] {
    const lista: string[] = [];
    if (!nome.trim()) lista.push('Dê um nome ao modelo.');
    if (campos.length === 0) lista.push('Um modelo sem campo nenhum não coleta nada.');
    const semRotulo = campos.filter((c) => !c.rotulo.trim()).length;
    if (semRotulo > 0) {
        lista.push(semRotulo === 1 ? 'Um campo está sem rótulo.' : `${semRotulo} campos estão sem rótulo.`);
    }
    /* Duas opções é o mínimo para haver escolha. Com uma só, a resposta já está
       decidida antes de o profissional abrir a ficha. */
    const semOpcoes = campos.filter((c) => TIPO_TEM_OPCOES.includes(c.tipo) && (c.opcoes ?? []).filter((o) => o.trim()).length < 2);
    if (semOpcoes.length > 0) {
        lista.push(`Campo de seleção precisa de pelo menos duas opções: ${semOpcoes.map((c) => c.rotulo.trim() || 'sem rótulo').join(', ')}.`);
    }

    /* Bloco do catálogo sem escala escolhida não desenha nada — e o profissional
       abriria a ficha com um espaço vazio no lugar da pergunta. */
    const semEscala = campos.filter((c) => TIPO_TEM_ESCALA.includes(c.tipo) && !(c.escala ?? c.tabela));
    if (semEscala.length > 0) {
        lista.push(`Escolha qual escala cada bloco usa: ${semEscala.map((c) => c.rotulo.trim() || 'sem rótulo').join(', ')}.`);
    }

    const tabelaVazia = campos.filter((c) => c.tipo === 'tabela'
        && ((c.colunas ?? []).filter((x) => x.trim()).length === 0 || (c.linhas ?? []).filter((x) => x.trim()).length === 0));
    if (tabelaVazia.length > 0) {
        lista.push(`Tabela precisa de ao menos uma linha e uma coluna: ${tabelaVazia.map((c) => c.rotulo.trim() || 'sem rótulo').join(', ')}.`);
    }
    return lista;
}

export function EditorDeModelo({ open, modelo, nomeInicial, camposIniciais, onClose, onSalvo }: EditorDeModeloProps) {
    const aviso = useAviso();
    const idNome = useId();
    const [nome, setNome] = useState('');
    const [campos, setCampos] = useState<CampoDeFicha[]>([]);
    const [previa, setPrevia] = useState(false);
    const [tentouSalvar, setTentouSalvar] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    /* Reabrir o editor tem de trazer o modelo de agora, não o da vez passada.
       Sem isto, editar A e depois B mostrava os campos de A. */
    useEffect(() => {
        if (!open) return;
        setNome(nomeInicial ?? modelo?.nome ?? '');
        setCampos((modelo?.campos ?? camposIniciais ?? []).map((c) => ({ ...c, opcoes: c.opcoes ? [...c.opcoes] : undefined })));
        setPrevia(false);
        setTentouSalvar(false);
        setErro(null);
    }, [open, modelo, nomeInicial, camposIniciais]);

    const alterar = (uid: string, mudanca: Partial<CampoDeFicha>) => {
        setCampos((atuais) => atuais.map((c) => (c.uid === uid ? { ...c, ...mudanca } : c)));
    };

    const adicionar = (tipo: TipoDeCampoDaFicha) => {
        setCampos((atuais) => [...atuais, campoNovo(tipo)]);
    };

    const remover = (uid: string) => setCampos((atuais) => atuais.filter((c) => c.uid !== uid));

    const duplicar = (campo: CampoDeFicha) => {
        setCampos((atuais) => {
            const i = atuais.findIndex((c) => c.uid === campo.uid);
            const copia: CampoDeFicha = { ...campo, uid: novoUid(), opcoes: campo.opcoes ? [...campo.opcoes] : undefined };
            const proximos = [...atuais];
            proximos.splice(i + 1, 0, copia);
            return proximos;
        });
    };

    /* Setas, não arraste: reordenar é ação, e arrastar com o mouse não é
       alcançável por teclado. */
    const mover = (uid: string, passo: -1 | 1) => {
        setCampos((atuais) => {
            const i = atuais.findIndex((c) => c.uid === uid);
            const j = i + passo;
            if (i < 0 || j < 0 || j >= atuais.length) return atuais;
            const proximos = [...atuais];
            const [item] = proximos.splice(i, 1);
            if (item) proximos.splice(j, 0, item);
            return proximos;
        });
    };

    const trocarTipo = (campo: CampoDeFicha, tipo: TipoDeCampoDaFicha) => {
        /* Trocar de tipo descarta a configuração que não se aplica: guardar
           opção, escala ou grade que a tela não mostra é dado que ninguém revê e
           que volta a aparecer se o tipo mudar de novo. O novo tipo entra com o
           padrão dele, para o bloco continuar utilizável no mesmo instante. */
        const modelo = campoNovo(tipo);
        alterar(campo.uid, {
            tipo,
            opcoes: TIPO_TEM_OPCOES.includes(tipo) ? (campo.opcoes ?? []) : undefined,
            escala: modelo.escala,
            tabela: modelo.tabela,
            colunas: modelo.colunas,
            linhas: modelo.linhas,
            sexo: modelo.sexo,
            protocolo: modelo.protocolo,
            faixas: modelo.faixas,
            rotulo: campo.rotulo.trim() || modelo.rotulo,
        });
    };

    const pendencias = problemas(nome, campos);
    const sujo = nome !== (nomeInicial ?? modelo?.nome ?? '') || JSON.stringify(campos) !== JSON.stringify(modelo?.campos ?? []);

    const salvar = async () => {
        setTentouSalvar(true);
        if (pendencias.length > 0) {
            setErro(null);
            return;
        }
        setSalvando(true);
        try {
            const limpos = campos.map((c) => ({
                ...c,
                rotulo: c.rotulo.trim(),
                opcoes: c.opcoes ? c.opcoes.map((o) => o.trim()).filter(Boolean) : undefined,
                /* Faixa sem rótulo não classifica nada e ainda ocuparia uma linha
                   na próxima edição. Sai aqui, como as opções em branco. */
                faixas: c.faixas ? c.faixas.map((f) => ({ ...f, rotulo: f.rotulo.trim() })).filter((f) => f.rotulo) : undefined,
            }));
            const salvo = await dados().salvarModeloDeFicha({ id: modelo?.id, nome, campos: limpos });
            aviso({
                title: modelo ? 'Modelo atualizado' : 'Modelo criado',
                description: `${salvo.nome} · versão ${salvo.versao} · ${limpos.length === 1 ? '1 campo' : `${limpos.length} campos`}`,
            });
            onSalvo();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar o modelo.');
        } finally {
            setSalvando(false);
        }
    };

    const usos = modelo?.usos ?? 0;

    return (
        <Modal
            open={open} onClose={onClose} width={880}
            title={modelo ? `Editar ${modelo.nome}` : 'Novo modelo de ficha'}
            description="O modelo é o que o profissional preenche na consulta. Alterar o modelo não altera ficha já preenchida."
            dirty={sujo}
            onSave={() => void salvar()}
            footer={
                <>
                    <span style={{ marginRight: 'auto', fontSize: 12, lineHeight: '16px', color: 'var(--text-tertiary)', textAlign: 'left', maxWidth: 340 }}>
                        {campos.length === 0 ? 'Nenhum campo ainda.' : `${campos.length === 1 ? '1 campo' : `${campos.length} campos`}${modelo ? ` · versão ${modelo.versao} hoje` : ''}`}
                    </span>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando}>{modelo ? 'Salvar alterações' : 'Criar modelo'}</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                {tentouSalvar && pendencias.length > 0 && (
                    <Alert tone="warning" title="Falta resolver antes de salvar" dismissible={false}>
                        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                            {pendencias.map((p) => <li key={p}>{p}</li>)}
                        </ul>
                    </Alert>
                )}

                {modelo && usos > 0 && (
                    <Alert tone="info" title={`${usos === 1 ? 'Uma ficha já foi preenchida' : `${usos} fichas já foram preenchidas`} com este modelo`} dismissible={false}>
                        Elas continuam sendo lidas com os rótulos que tinham no dia. Salvar aqui cria a versão {modelo.versao + 1} e vale das próximas em diante.
                    </Alert>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                    <FormField label="Nome do modelo" required htmlFor={idNome} style={{ flex: '1 1 240px', minWidth: 0 }}>
                        <Input id={idNome} value={nome} onChange={setNome} placeholder="Anamnese facial" />
                    </FormField>
                    <Switch checked={previa} onChange={setPrevia} label="Pré-visualizar" />
                </div>

                {previa ? (
                    <PreviaDoModelo campos={campos} />
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 200px) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Dois grupos, porque são duas decisões diferentes: o genérico
                            monta qualquer pergunta; o do profissional traz vocabulário
                            clínico publicado, que não se edita como campo livre. */}
                            <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>Campos gerais</p>
                            {PALETA.filter((b) => !b.doProfissional).map((bloco) => (
                                <Tooltip key={bloco.tipo} label={bloco.ajuda}>
                                    <Button
                                        size="sm" variant="secondary" onClick={() => adicionar(bloco.tipo)}
                                        iconLeft={<Icon name={bloco.icone} size={15} />}
                                        style={{ justifyContent: 'flex-start', width: '100%' }}
                                    >
                                        {bloco.nome}
                                    </Button>
                                </Tooltip>
                            ))}

                            <p style={{ margin: '8px 0 0', fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>Do profissional</p>
                            {PALETA.filter((b) => b.doProfissional).map((bloco) => (
                                <Tooltip key={bloco.tipo} label={bloco.ajuda}>
                                    <Button
                                        size="sm" variant="secondary" onClick={() => adicionar(bloco.tipo)}
                                        iconLeft={<Icon name={bloco.icone} size={15} />}
                                        style={{ justifyContent: 'flex-start', width: '100%' }}
                                    >
                                        {bloco.nome}
                                    </Button>
                                </Tooltip>
                            ))}
                            {/* A ausência dita em voz alta, para não parecer esquecimento
                                — e some junto com ela, porque "ainda fora: nada" é ruído. */}
                            {PENDENTES.length > 0 && (
                                <p style={{ margin: '6px 0 0', fontSize: 11.5, lineHeight: '16px', color: 'var(--text-tertiary)' }}>
                                    Ainda fora deste editor: {PENDENTES.join('; ')}.
                                </p>
                            )}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {campos.length === 0 ? (
                                <p style={{ margin: 0, padding: '28px 16px', textAlign: 'center', border: '1.5px dashed var(--border-strong)', borderRadius: 'var(--radius-card)', fontSize: 13, color: 'var(--text-tertiary)' }}>
                                    Escolha um campo à esquerda para começar.
                                </p>
                            ) : campos.map((campo, i) => (
                                <CampoEmEdicao
                                    key={campo.uid}
                                    campo={campo} indice={i} total={campos.length}
                                    destacarFalta={tentouSalvar}
                                    onAlterar={(m) => alterar(campo.uid, m)}
                                    onTrocarTipo={(t) => trocarTipo(campo, t)}
                                    onMover={(p) => mover(campo.uid, p)}
                                    onDuplicar={() => duplicar(campo)}
                                    onRemover={() => remover(campo.uid)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}

interface CampoEmEdicaoProps {
    campo: CampoDeFicha;
    indice: number;
    total: number;
    destacarFalta: boolean;
    onAlterar: (mudanca: Partial<CampoDeFicha>) => void;
    onTrocarTipo: (tipo: TipoDeCampoDaFicha) => void;
    onMover: (passo: -1 | 1) => void;
    onDuplicar: () => void;
    onRemover: () => void;
}

function CampoEmEdicao({ campo, indice, total, destacarFalta, onAlterar, onTrocarTipo, onMover, onDuplicar, onRemover }: CampoEmEdicaoProps) {
    const idRotulo = useId();
    const idTipo = useId();
    const opcoes = campo.opcoes ?? [];
    const temOpcoes = TIPO_TEM_OPCOES.includes(campo.tipo);
    const semRotulo = destacarFalta && !campo.rotulo.trim();
    const nomeDoCampo = campo.rotulo.trim() || `campo ${indice + 1}`;

    const mudarOpcao = (i: number, valor: string) => {
        const proximas = [...opcoes];
        proximas[i] = valor;
        onAlterar({ opcoes: proximas });
    };

    return (
        <div style={{ border: `1px solid ${semRotulo ? 'var(--warning-strong)' : 'var(--border)'}`, borderRadius: 'var(--radius-card)', padding: 14, background: 'var(--surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Badge tone="neutral">{indice + 1}</Badge>
                <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-secondary)' }}>{NOME_DO_TIPO[campo.tipo]}</span>
                <IconButton size="sm" icon={<Icon name="arrow-up" size={14} />} label={`Mover ${nomeDoCampo} para cima`} disabled={indice === 0} onClick={() => onMover(-1)} />
                <IconButton size="sm" icon={<Icon name="arrow-down" size={14} />} label={`Mover ${nomeDoCampo} para baixo`} disabled={indice === total - 1} onClick={() => onMover(1)} />
                <IconButton size="sm" icon={<Icon name="copy" size={14} />} label={`Duplicar ${nomeDoCampo}`} onClick={onDuplicar} />
                <IconButton size="sm" icon={<Icon name="trash-2" size={14} />} label={`Remover ${nomeDoCampo}`} onClick={onRemover} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 12 }}>
                <FormField label="Rótulo" required htmlFor={idRotulo} error={semRotulo ? 'Sem rótulo, ninguém sabe o que responder.' : undefined}>
                    <Input id={idRotulo} value={campo.rotulo} onChange={(v) => onAlterar({ rotulo: v })} placeholder="Queixa principal" />
                </FormField>
                <FormField label="Tipo" htmlFor={idTipo}>
                    <Select
                        id={idTipo} value={campo.tipo}
                        onChange={(v) => onTrocarTipo(v as TipoDeCampoDaFicha)}
                        options={PALETA.map((b) => ({ value: b.tipo, label: b.nome }))}
                    />
                </FormField>
            </div>

            <div style={{ marginTop: 10 }}>
                <Checkbox
                    checked={Boolean(campo.obrigatorio)}
                    onChange={(v) => onAlterar({ obrigatorio: v })}
                    label="Obrigatório"
                    ariaLabel={`${nomeDoCampo} é obrigatório`}
                />
            </div>

            {TIPO_TEM_ESCALA.includes(campo.tipo) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                    <FormField
                        label={campo.tipo === 'tabela-selecionavel' ? 'Qual tabela' : 'Qual escala'}
                        hint="As opções, os critérios e a arte vêm do pacote clínico — não se editam aqui."
                    >
                        <Select
                            value={campo.escala ?? campo.tabela ?? ''}
                            aria-label={`Escala de ${nomeDoCampo}`}
                            onChange={(v) => {
                                /* Trocar a escala troca o rótulo junto, a menos que
                                   alguém já tenha escrito o seu: o nome da escala é o
                                   que o profissional procura na ficha. */
                                const nomes = campo.tipo === 'escala'
                                    ? ESCALAS_ILUSTRADAS.find((e) => e.slug === v)?.nome
                                    : campo.tipo === 'escala-encadeada'
                                        ? ESCALAS_ENCADEADAS.find((e) => e.slug === v)?.rotulo
                                        : TABELAS_SELECIONAVEIS.find((t) => t.slug === v)?.rotulo;
                                const anterior = rotuloDoCatalogo(campo);
                                onAlterar({
                                    ...(campo.tipo === 'tabela-selecionavel' ? { tabela: v } : { escala: v }),
                                    ...(nomes && (!campo.rotulo.trim() || campo.rotulo.trim() === anterior) ? { rotulo: nomes } : {}),
                                });
                            }}
                            options={
                                campo.tipo === 'escala'
                                    ? ESCALAS_ILUSTRADAS.map((e) => ({
                                        value: e.slug,
                                        label: e.semArte ? `${e.nome} — arte incompleta` : e.nome,
                                    }))
                                    : campo.tipo === 'escala-encadeada'
                                        ? ESCALAS_ENCADEADAS.map((e) => ({ value: e.slug, label: e.rotulo }))
                                        : TABELAS_SELECIONAVEIS.map((t) => ({ value: t.slug, label: t.rotulo }))
                            }
                        />
                    </FormField>
                </div>
            )}

            {TIPO_TEM_PROTOCOLO.includes(campo.tipo) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                    <FormField
                        label="Qual protocolo"
                        hint="A equação é publicada e vem citada na ficha. Trocar o protocolo troca as dobras que se mede."
                    >
                        <Select
                            value={campo.protocolo ?? 'somatorio'}
                            aria-label={`Protocolo de ${nomeDoCampo}`}
                            onChange={(v) => onAlterar({ protocolo: v })}
                            options={PROTOCOLOS_DE_ADIPOMETRIA.map((p) => ({ value: p.slug, label: p.rotulo }))}
                        />
                    </FormField>
                </div>
            )}

            {TIPO_TEM_PROTOCOLO.includes(campo.tipo) && protocoloPorSlug(campo.protocolo)?.slug !== 'somatorio' && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                    <ListaDeFaixas campo={campo} nomeDoCampo={nomeDoCampo} onAlterar={onAlterar} />
                </div>
            )}

            {TIPO_TEM_SEXO.includes(campo.tipo) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                    <FormField
                        label="Sexo"
                        hint="Sem ele a conta sai igual; o que some é a classificação que depende de sexo."
                    >
                        <Select
                            value={campo.sexo ?? ''} aria-label={`Sexo em ${nomeDoCampo}`}
                            onChange={(v) => onAlterar({ sexo: v as CampoDeFicha['sexo'] })}
                            options={[
                                { value: '', label: 'Não informar' },
                                { value: 'feminino', label: 'Feminino' },
                                { value: 'masculino', label: 'Masculino' },
                            ]}
                        />
                    </FormField>
                </div>
            )}

            {campo.tipo === 'tabela' && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <ListaDeTitulos
                        titulo="Colunas" nomeDoCampo={nomeDoCampo}
                        itens={campo.colunas ?? []} onAlterar={(v) => onAlterar({ colunas: v })}
                    />
                    <ListaDeTitulos
                        titulo="Linhas" nomeDoCampo={nomeDoCampo}
                        itens={campo.linhas ?? []} onAlterar={(v) => onAlterar({ linhas: v })}
                    />
                </div>
            )}

            {temOpcoes && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>Opções</p>
                    {opcoes.map((opcao, i) => (
                        <div key={`${campo.uid}-op-${String(i)}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Input
                                value={opcao} onChange={(v) => mudarOpcao(i, v)}
                                aria-label={`Opção ${String(i + 1)} de ${nomeDoCampo}`}
                                placeholder={`Opção ${String(i + 1)}`}
                            />
                            <IconButton
                                size="sm" icon={<Icon name="x" size={14} />}
                                label={`Remover opção ${String(i + 1)} de ${nomeDoCampo}`}
                                onClick={() => onAlterar({ opcoes: opcoes.filter((_, k) => k !== i) })}
                            />
                        </div>
                    ))}
                    <Button size="sm" variant="ghost" iconLeft={<Icon name="plus" size={14} />} onClick={() => onAlterar({ opcoes: [...opcoes, ''] })}>
                        Adicionar opção
                    </Button>
                </div>
            )}
        </div>
    );
}

/** O nome que o catálogo dá ao bloco — para saber se o rótulo foi personalizado. */
function rotuloDoCatalogo(campo: CampoDeFicha): string | undefined {
    if (campo.tipo === 'escala') return ESCALAS_ILUSTRADAS.find((e) => e.slug === campo.escala)?.nome;
    if (campo.tipo === 'escala-encadeada') return ESCALAS_ENCADEADAS.find((e) => e.slug === campo.escala)?.rotulo;
    if (campo.tipo === 'tabela-selecionavel') return TABELAS_SELECIONAVEIS.find((t) => t.slug === campo.tabela)?.rotulo;
    return undefined;
}

/** Os títulos das colunas ou das linhas de uma tabela livre. */
/**
 * As faixas com que a clínica lê o percentual de gordura.
 *
 * Existe vazia de propósito. **Não há tabela oficial** de percentual de gordura
 * como a da OMS para o IMC: ACSM, Pollock & Wilmore e Lohman discordam entre si,
 * e escolher uma pelo produto seria escolher conduta pela clínica. Então o
 * produto oferece o lugar, e a clínica traz a tabela que adota — que é a única
 * que ela vai saber defender quando alguém perguntar de onde veio o "Aceitável".
 *
 * A ficha guarda a definição do dia, então trocar a tabela depois não reescreve
 * a leitura de prontuário já emitido.
 */
function ListaDeFaixas({ campo, nomeDoCampo, onAlterar }: {
    campo: CampoDeFicha; nomeDoCampo: string; onAlterar: (mudanca: Partial<CampoDeFicha>) => void;
}) {
    const faixas = campo.faixas ?? [];
    const mudar = (i: number, mudanca: Partial<FaixaDeClassificacao>) => {
        onAlterar({ faixas: faixas.map((f, k) => (k === i ? { ...f, ...mudanca } : f)) });
    };

    return (
        <FormField
            label="Faixas de leitura do percentual"
            hint="Opcional, e vazia por padrão: sem elas sai o número sem classificação. Não há tabela oficial — use a que a clínica adota, e faça a última cobrir o topo."
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {faixas.map((faixa, i) => (
                    <div key={`${campo.uid}-faixa-${String(i)}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>até</span>
                        <Input
                            size="sm" type="number" style={{ width: 88 }}
                            value={String(faixa.ate)}
                            aria-label={`Limite da faixa ${String(i + 1)} de ${nomeDoCampo}`}
                            onChange={(v) => mudar(i, { ate: Number(v) })}
                        />
                        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>% →</span>
                        <Input
                            size="sm" value={faixa.rotulo}
                            placeholder="Como a clínica chama esta faixa"
                            aria-label={`Rótulo da faixa ${String(i + 1)} de ${nomeDoCampo}`}
                            onChange={(v) => mudar(i, { rotulo: v })}
                        />
                        <IconButton
                            size="sm" icon={<Icon name="x" size={13} />}
                            label={`Remover faixa ${String(i + 1)} de ${nomeDoCampo}`}
                            onClick={() => onAlterar({ faixas: faixas.filter((_, k) => k !== i) })}
                        />
                    </div>
                ))}
                <Button
                    size="sm" variant="ghost" iconLeft={<Icon name="plus" size={13} />}
                    onClick={() => onAlterar({ faixas: [...faixas, { ate: (faixas[faixas.length - 1]?.ate ?? 10) + 5, rotulo: '' }] })}
                >
                    Adicionar faixa
                </Button>
            </div>
        </FormField>
    );
}

function ListaDeTitulos({ titulo, nomeDoCampo, itens, onAlterar }: {
    titulo: string; nomeDoCampo: string; itens: string[]; onAlterar: (itens: string[]) => void;
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{titulo}</p>
            {itens.map((item, i) => (
                <div key={`${titulo}-${String(i)}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Input
                        size="sm" value={item}
                        aria-label={`${titulo} ${String(i + 1)} de ${nomeDoCampo}`}
                        onChange={(v) => onAlterar(itens.map((x, k) => (k === i ? v : x)))}
                    />
                    <IconButton
                        size="sm" icon={<Icon name="x" size={13} />}
                        label={`Remover ${titulo.toLowerCase()} ${String(i + 1)} de ${nomeDoCampo}`}
                        onClick={() => onAlterar(itens.filter((_, k) => k !== i))}
                    />
                </div>
            ))}
            <Button size="sm" variant="ghost" iconLeft={<Icon name="plus" size={13} />} onClick={() => onAlterar([...itens, `${titulo.slice(0, -1)} ${String(itens.length + 1)}`])}>
                Adicionar
            </Button>
        </div>
    );
}

/**
 * A ficha como o profissional vai vê-la.
 *
 * Os controles são os mesmos do preenchimento, e por isso a prévia revela o que
 * a maquete esconderia: campo obrigatório sem opção, rótulo em branco, seleção
 * com uma única alternativa.
 */
export function PreviaDoModelo({ campos }: { campos: CampoDeFicha[] }) {
    if (campos.length === 0) {
        return (
            <p style={{ margin: 0, padding: '28px 16px', textAlign: 'center', border: '1.5px dashed var(--border-strong)', borderRadius: 'var(--radius-card)', fontSize: 13, color: 'var(--text-tertiary)' }}>
                Sem campos, não há o que pré-visualizar.
            </p>
        );
    }
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 16, background: 'var(--surface-secondary)', borderRadius: 'var(--radius-card)' }}>
            {campos.map((campo, i) => {
                const rotulo = campo.rotulo.trim() || `Campo ${String(i + 1)} — sem rótulo`;
                return (
                    <FormField key={campo.uid} label={rotulo} required={campo.obrigatorio}>
                        {/* O MESMO controle do preenchimento, desligado. Uma segunda
                            implementação para a prévia divergiria, e a prévia passaria a
                            mostrar uma ficha que não é a que o profissional vai ver — que
                            é exatamente o que ela existe para evitar. */}
                        <BlocoDeResposta campo={campo} valor={undefined} onResponder={() => undefined} desabilitado />
                    </FormField>
                );
            })}
        </div>
    );
}
